import { ModelBase } from './base';
import { Listing, ListingType } from './listing';
import { BasicProfile } from './profile';
import { AppDate } from '../envTypes';
import { ListingPriceSource } from './order';
import { BalanceChangePayment } from './payout';

// /tokenListings/{id}
export interface TokenListing extends ModelBase {
  type: ListingType.TokenPack;
  name: string;
  price: number;
  tokens: number;
  bonusTokens?: number;
  active: boolean;
  sort: number;
}

// /tokenPacks/{id}
export interface TokenPack extends ModelBase {
  name: string;
  price: number;
  tokens: number;
  bonusTokens?: number;
  active: boolean;
  sort: number;
}

/**
 * Products that can be purchased with tokens.
 * Corresponds to `TOKEN_COSTS` keys in `src/flows/tokens/constants.ts`.
 */
export enum TokenCostType {
  // avatarme
  AIPhoto = 'AI_PHOTO',
  AIVideo5s = 'AI_VIDEO_5S',
  AIVideo10s = 'AI_VIDEO_10S',
  AIVideo15s = 'AI_VIDEO_15S',
  // per second of a studio video whose duration the buyer picks
  AIVideoPerSecond = 'AI_VIDEO_PER_SECOND',
  AIVideo480pPerSecond = 'AI_VIDEO_480P_PER_SECOND',
  AIVideo720pPerSecond = 'AI_VIDEO_720P_PER_SECOND',
  AIVideo1080pPerSecond = 'AI_VIDEO_1080P_PER_SECOND',
  AIStoryboardPreview = 'AI_STORYBOARD_PREVIEW',
  AIVideoCustomMotion = 'AI_VIDEO_CUSTOM_MOTION',
  LipSyncAddon = 'LIP_SYNC_ADDON',
  PhotoShoot = 'PHOTO_SHOOT',
  // ai-generated pdf
  FullyAutoPDF = 'FULLY_AUTO_PDF',
  AIAssistedPDF = 'AI_ASSISTED_PDF',
  EditPDF = 'EDIT_PDF',
  // echo-for-commentchat
  // Single DM Response, DM-Agent (CommentChat)
  AIDMResponseCommentchat = 'AI_DM_RESPONSE_COMMENTCHAT',
  // Single Comment Response, DM-Agent (CommentChat)
  AICommentResponseCommentchat = 'AI_COMMENT_RESPONSE_COMMENTCHAT',
  // Single Comment Response, Social Monitoring Agent
  EchoCommentGeneration = 'ECHO_COMMENT_GENERATION',
  // DM Offer Analysis
  DMOfferAnalysis = 'DM_OFFER_ANALYSIS',
  AICommentAnalysis = 'AI_COMMENT_ANALYSIS',
  AIPostAnalysis = 'AI_POST_ANALYSIS',
  AICommentIntentOfferMatching = 'AI_COMMENT_INTENT_OFFER_MATCHING',
  AITemplateCustomization = 'ai-template-customization',
  FollowerProfileScrape = 'FOLLOWER_PROFILE_SCRAPE',
  // Comments on a collab post another account published, read by scraping —
  // the Graph API serves them to the publisher only. One unit per post read.
  CollabPostCommentScrape = 'COLLAB_POST_COMMENT_SCRAPE',
  // growth-rx (ECHO-ME agent 7)
  GrowthRxDiagnostic = 'GROWTH_RX_DIAGNOSTIC',
  GrowthRxScript = 'GROWTH_RX_SCRIPT',
  /**
   * The diagnostic + plan chain run AHEAD of the creator, the moment their ECHO
   * subscription is paid for, so Growth RX opens onto a finished plan instead of
   * a ~68 second wait. Same work as a diagnostic, half the price — see
   * `GROWTH_RX_PREWARM_CHARGE_RATIO` in the ai-service.
   *
   * It is a cost type of its own because an order carries a QUANTITY, never a
   * price: quantity is a whole number of units, so half a unit can only be
   * expressed as a unit priced at half.
   */
  GrowthRxPrewarm = 'GROWTH_RX_PREWARM',
  /**
   * One Growth RX post score: a published post read and scored seven days
   * out, by the creator's own "Score this post" or by the automatic sweep they
   * opted into. One unit per scored post, charged by popstore-ai-service after
   * the score lands (`TOKEN_VARIANT_SKU.GROWTH_RX_POST_SCORE`).
   */
  GrowthRxPostScore = 'GROWTH_RX_POST_SCORE',
  VideoChapter = 'VIDEO_CHAPTER',
}

/**
 * The unit `TokenCost.tokenCost` (and `salePrice`) is expressed in.
 */
export enum TokenCostUnit {
  // a flat price per unit ordered
  Tokens = 'TOKENS',
  // a price per second: the order's unit price is tokenCost x the requested durationSeconds
  TokensPerSecond = 'TOKENS_PER_SECOND',
  TokensPerMinute = 'TOKENS_PER_MINUTE',
}

// /tokenCosts/{slug}
export interface TokenCost extends ModelBase {
  name: string;
  provider: string;
  maxDuration: number;
  slug: string;
  tokenCost: number;
  // deprecated
  tokenCostType?: TokenCostType;
  active: boolean;
  sort: number;
  operatorId?: string;
  operator?: BasicProfile;
  salePrice?: number;
  saleStartDate?: AppDate;
  saleEndDate?: AppDate;
  listingType?: ListingType;
  // absent = TokenCostUnit.Tokens
  unit?: TokenCostUnit;
}

export enum BonusTokenAction {
  AddSocials = 'ADD_SOCIALS',
}

export enum TokenBalanceOperation {
  // + tokens, create money-to-token order
  TopupToken = 'TOPUP_TOKEN',
  // - tokens, create consuming-token order
  ConsumeToken = 'CONSUME_TOKEN',
  // + tokens, refund consuming-token order
  RefundToken = 'REFUND_TOKEN',
  // + tokens, grant by admin
  GrantToken = 'GRANT_TOKEN',
  // - tokens, revoke by admin
  RevokeToken = 'REVOKE_TOKEN',
  // + tokens, Bonus for Adding Socials
  BonusAddingSocials = 'BONUS_ADDING_SOCIALS',
  // + tokens, Bonus for Sponsored Creator
  BonusSponsoredCreator = 'BONUS_SPONSORED_CREATOR',
  /**
   * @deprecated replaced by `BonusToken`
   * + tokens, AI Echo-Me subscription grant
   */
  AiEchoMeSubscriptionGrant = 'AI_ECHO_ME_SUBSCRIPTION_GRANT',
  // + tokens, AI Echo-Me subscription grant
  BonusToken = 'BONUS_TOKEN',
  // - tokens, AI Echo-Me subscription due to expire/cancel/refund
  ExpireToken = 'EXPIRE_TOKEN',
  /**
   * @deprecated replaced by `ConsumeToken`
   * - tokens, Real Estate Agent(Echo Bot)
   */
  RealEstateAgent = 'REAL_ESTATE_AGENT',
  /**
   * @deprecated replaced by `ConsumeToken`
   * - tokens, Commentchat
   */
  Commentchat = 'COMMENTCHAT',
}

export enum TokenBalanceSourceType {
  // /orders/{id}
  Order = 'ORDER',
  // /tokenOrders/{id}
  TokenOrder = 'TOKEN_ORDER',
  // /profiles/{id}
  USER = 'USER',
  // @deprecated, /profiles/{id}
  BonusAddingSocials = 'BONUS_ADDING_SOCIALS',
  // subscription invoice
  AiEchoMeSubscription = 'AI_ECHO_ME_SUBSCRIPTION',
  // paid credit add-on purchase attached to a SaaS subscription
  SubscriptionAddOn = 'SUBSCRIPTION_ADD_ON',
  // real estate listing
  RealEstateListing = 'REAL_ESTATE_LISTING',
  // commentchat
  Commentchat = 'COMMENTCHAT',
}

export interface TokenBalanceChangeTrack {
  tokenBalance: number;
}

export interface TokenBalanceChangeCost {
  type?: TokenCostType;
  // TokenCost.slug
  slug?: string;
  // TokenCost.name
  name?: string;
  value: number;
}

export type TokenBalanceSource =
  | {
      operation: TokenBalanceOperation.TopupToken;
      type: TokenBalanceSourceType.Order;
    }
  | {
      operation:
        | TokenBalanceOperation.ConsumeToken
        | TokenBalanceOperation.RefundToken;
      type:
        | TokenBalanceSourceType.TokenOrder
        | TokenBalanceSourceType.RealEstateListing
        | TokenBalanceSourceType.Commentchat;
    }
  | {
      operation:
        | TokenBalanceOperation.GrantToken
        | TokenBalanceOperation.RevokeToken;
      type: TokenBalanceSourceType.USER;
    }
  | {
      operation: TokenBalanceOperation.BonusAddingSocials;
      type: TokenBalanceSourceType.BonusAddingSocials;
    }
  | {
      operation: TokenBalanceOperation.BonusSponsoredCreator;
      type: TokenBalanceSourceType.USER;
    }
  | {
      operation:
        | TokenBalanceOperation.BonusToken
        | TokenBalanceOperation.ExpireToken;
      type: TokenBalanceSourceType.AiEchoMeSubscription;
    }
  | {
      // Paid credit add-on purchase attached to a SaaS subscription
      operation: TokenBalanceOperation.TopupToken;
      type: TokenBalanceSourceType.SubscriptionAddOn;
    };

export enum TokenBalanceOperationGroup {
  // TopupToken
  Topup = 'TOPUP',
  // ConsumeToken
  Consume = 'CONSUME',
  // RefundToken
  Refund = 'REFUND',
  // GrantToken, RevokeToken
  Manual = 'MANUAL',
  // BonusAddingSocials
  Bonus = 'BONUS',
  // BonusToken, ExpireToken
  Subscription = 'SUBSCRIPTION',
}

export const TokenBalanceOperationGroupMap: Record<
  TokenBalanceOperationGroup,
  TokenBalanceOperation[]
> = {
  [TokenBalanceOperationGroup.Topup]: [TokenBalanceOperation.TopupToken],
  [TokenBalanceOperationGroup.Consume]: [TokenBalanceOperation.ConsumeToken],
  [TokenBalanceOperationGroup.Refund]: [TokenBalanceOperation.RefundToken],
  [TokenBalanceOperationGroup.Manual]: [
    TokenBalanceOperation.GrantToken,
    TokenBalanceOperation.RevokeToken,
  ],
  [TokenBalanceOperationGroup.Bonus]: [
    TokenBalanceOperation.BonusAddingSocials,
    TokenBalanceOperation.BonusSponsoredCreator,
  ],
  [TokenBalanceOperationGroup.Subscription]: [
    TokenBalanceOperation.AiEchoMeSubscriptionGrant,
    TokenBalanceOperation.BonusToken,
    TokenBalanceOperation.ExpireToken,
  ],
};

// /readonlyProfiles/{sellerId}/tokenBalanceChanges/{id}
export interface TokenBalanceChange extends ModelBase {
  before: TokenBalanceChangeTrack & {
    balanceChangeId: string | null;
    index?: number;
  };
  operation: TokenBalanceOperation;
  amount: number;
  // costs for token gated content
  costs?: TokenBalanceChangeCost[];
  sourceType: TokenBalanceSourceType;
  sourceId: string;
  after: TokenBalanceChangeTrack;
  index: number;
  note?: string;
  noteBy?: string;
  commentSold?:
    | {
        success: true;
        syncedAt: AppDate;
      }
    | {
        success: false;
        failedAt: AppDate;
        failureReason: string;
      };
  // for searching by user name
  userProfile?: BasicProfile;
  // Admin's email or System
  grantedBy?: string;
  // meta data from caller
  meta?: Record<string, any>;
  payment?: BalanceChangePayment;
  /**
   * Low-credit reminder cycle state, carried forward on every change.
   *
   * `baseline` is the denominator for the low-credit percentage — the amount of
   * usable tokens for the current cycle. It is reset to the full balance on each
   * top-up (purchase), reduced when tokens expire/are revoked, and raised when
   * tokens are granted/refunded, so `after.tokenBalance / baseline` always reflects
   * the fraction the creator has actually consumed.
   *
   * `notifiedThresholds` records which percent-consumed reminders (e.g. 75/80/90)
   * have already been sent this cycle so each fires at most once.
   */
  lowCreditCycle?: {
    baseline: number;
    notifiedThresholds: number[];
  };
}

export enum TokenOrderStatus {
  Created = 'CREATED',
  Canceled = 'CANCELED',
}

export interface TokenOrderPriceComponents {
  buyerPaidAmount: number;
  total: number;
  subtotal: number;
  listingPrice: number;
  listingOriginPrice?: number;
  listingPriceSource: ListingPriceSource;
  listingQuantity: number;
  listingSubTotal?: number;
  listingDiscountedSubTotal: number;
  listingDiscountPercent?: number;
}

export interface TokenOrderChargeDistribution {
  buyerPaidAmount: number;
  subtotal: number;
  listingQuantity: number;
  listingDiscountedSubTotal: number;
}

export enum TokenOrderRefundStatus {
  PartiallyRefunded = 'PARTIALLY_REFUNDED',
  FullyRefunded = 'FULLY_REFUNDED',
}

// /tokenOrders/{id}
export interface TokenOrder extends ModelBase {
  input: any;
  status: TokenOrderStatus;
  refundStatus?: TokenOrderRefundStatus;
  buyerId: string;
  buyer: BasicProfile;
  listingId: string;
  variantId?: string;
  listingSnapshot: Listing;
  quantity: number;
  total: number; // total in tokens without any refunds
  buyerPaidAmount: number; // paid in tokens
  priceComponents: TokenOrderPriceComponents;
  orderChargeDistribution: TokenOrderChargeDistribution;
  refund?: number; // refunds in tokens
  refunds?: TokenOrderRefundRecord[];
}

export type TokenOrderRefundDetail = {
  subtotal: number;
  listingQuantity?: number;
  listingDiscountedSubTotal: number;
};

export enum TokenOrderRefundType {
  Cancel = 'CANCEL',
  Listing = 'LISTING',
}

export type TokenOrderRefundRecord = {
  detail: TokenOrderRefundDetail;
  types: TokenOrderRefundType[];
  shouldFullyRefund?: boolean;
  shouldSubtotalFullyRefund?: boolean;
  reason?: string;
  operatorId?: string;
  operator?: BasicProfile;
} & ModelBase;
